import React, { useState, useMemo, useEffect, useRef } from "react";
import Select from "react-select";
import { api, getAuthHeaders } from "../utils/api";
import { useUser } from "../redux/useUser";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../redux/updateUserSlice";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import AssetDistributionChart from "../components/AssetDistributionChart";
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Clock,
  ArrowUpRight,
  ArrowUp,
  ClockFading,
  ChevronsDown,
  ChevronsUp,
} from "lucide-react";
import Layout from "../components/Layout";
import Loader from "../components/Loader";
import CurrencyNotification from "../components/CurrencyNotification";
import MobileVerificationModal from "../components/MobileVerificationModal";
import "../styles/dashboard.css";
import InfoTooltip from "../components/InfoTooltip";
import { getCurrencySymbol } from "../utils/getCurrencySymbol";
import { formatLargeNumber } from "../utils/formatNumber";

const DASHBOARD_COMPARISON_CHART_HEIGHT = 220;
const DASHBOARD_COMPARISON_CHART_MARGIN = {
  top: 8,
  right: 12,
  left: 8,
  bottom: 0,
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeHover, setActiveHover] = useState(null);

  // Weekly: always show Mon-Sun
  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const GaugeChart = ({ percentage, color, currency }) => {
    const pct = Number(percentage);
    const clamped = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;
    const circumference = 2 * Math.PI * 45;
    const strokeDashoffset = circumference - (clamped / 100) * circumference;

    const formatPercentage = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return "0 %";
      
      let valueStr = "";
      let suffix = "";
      const isINR = currency === "INR";
      const absNum = Math.abs(num);

      if (isINR) {
        if (absNum >= 10000000) {
          valueStr = (num / 10000000).toFixed(1);
          suffix = " Cr %";
        } else if (absNum >= 100000) {
          valueStr = (num / 100000).toFixed(1);
          suffix = " L %";
        } else if (absNum >= 1000) {
          valueStr = (num / 1000).toFixed(1);
          suffix = " K %";
        } else {
          valueStr = num.toFixed(1);
          suffix = " %";
        }
      } else {
        if (absNum >= 1000000) {
          valueStr = (num / 1000000).toFixed(1);
          suffix = " M %";
        } else if (absNum >= 1000) {
          valueStr = (num / 1000).toFixed(1);
          suffix = " K %";
        } else {
          valueStr = num.toFixed(1);
          suffix = " %";
        }
      }
      valueStr = valueStr.replace(/\.0$/, "");
      return `${valueStr}${suffix}`;
    };

    const textVal = formatPercentage(percentage);
    // Dynamically adjust font-size based on text length to fit circle bounds
    const fontSize = textVal.length > 6 ? 11 : textVal.length > 5 ? 13 : textVal.length > 4 ? 15 : 17;

    return (
      <svg width="80" height="80" viewBox="0 0 100 100" className="gauge-chart" role="img" aria-label={`${textVal} change vs last month`}>
        <title>{`${textVal} change vs last month`}</title>
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          className="gauge-base"
          strokeWidth="7"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
        />
        <text
          x="50"
          y="49"
          textAnchor="middle"
          fontSize={fontSize}
          fontWeight="600"
          fill={color}
          dy="2"
        >
          {textVal}
        </text>
        <text
          x="50"
          y="63"
          textAnchor="middle"
          fontSize="7"
          fontWeight="500"
          fill="#8e95a9"
        >
          vs last mo
        </text>
      </svg>
    );
  };
  const HalfCircleGauge = ({ percentage }) => {
    // coerce to number and clamp 0..100, handle null/undefined
    const pct = Number.isFinite(Number(percentage)) ? Number(percentage) : 0;
    const clamped = Math.max(0, Math.min(100, pct));
    const circumference = Math.PI * 50;
    const strokeDashoffset = circumference - (clamped / 100) * circumference;

    return (
      <svg
        className="half-gauge"
        width="100%"
        height="60" // use numeric height to avoid the SVG error
        viewBox="16 16 108 60"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient
            id="gaugeGradient"
            x1="0%"
            y1="100%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#6EADFF" />
            <stop offset="100%" stopColor="#3733FF" />
          </linearGradient>
        </defs>

        <path
          d="M 20 70 A 50 50 0 0 1 120 70"
          fill="none"
          stroke="#58648E"
          strokeWidth="3.5"
          strokeDasharray="0.5,1.5"
        />

        <path
          d="M 20 70 A 50 50 0 0 1 120 70"
          fill="none"
          stroke="url(#gaugeGradient)"
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />

        <text
          x="70"
          y="55"
          textAnchor="middle"
          fontSize="16"
          fontWeight="500"
          fill="#2B3674"
        >
          {clamped}%
        </text>
      </svg>
    );
  };

  const kpiData = {
    netWorth: { value: 5672, change: 74, income: 14 },
    totalAssets: { value: 240, change: 56, income: 12 },
    totalLiabilities: { value: 160, change: 34, income: 8 },
  };
  // Asset vs Liability period state (move to top for correct initialization)
  // (Already declared at top, remove duplicate)

  // Asset Distribution chart state (move to top for correct initialization)
  const [assetDistributionChart, setAssetDistributionChart] = useState([]);
  // Use static list of all valid asset types for dropdown
  const assetTypeOptions = [
    "all",
    "homeProperty",
    "vehicles",
    "preciousMetals",
    "electronics",
    "furnitureArt",
    "cash",
    "importantPapers",
    "bankAccounts",
    "fixedDeposits",
    "insurance",
    "mutualFunds",
    "stocks",
    "crypto",
    "websitesDomains",
    "cloudStorage",
  ];
  // Asset vs Liability Breakdown state
  // Pie chart color palette for Asset Distribution
  const pieColors = [
    "#FF6B9D",
    "#4D9FEC",
    "#FF9F43",
    "#26C6DA",
    "#9B8CFF",
    "#67748E",
  ];
  const [assetLiabilityBreakdown, setAssetLiabilityBreakdown] = useState([]);
  const [assetLiabilityBreakdownLoading, setAssetLiabilityBreakdownLoading] =
    useState(false);
  const [assetLiabilityBreakdownError, setAssetLiabilityBreakdownError] =
    useState(null);

  // Calculate total assets and liabilities from assetLiabilityBreakdown array
  const totalAssets = assetLiabilityBreakdown.reduce(
    (sum, item) => sum + (Number(item.assets) || 0),
    0,
  );
  const totalLiabilities = assetLiabilityBreakdown.reduce(
    (sum, item) =>
      sum + (Number(item.liabilities) || Number(item.liability) || 0),
    0,
  );
  const [assetLiabilityPeriod, setAssetLiabilityPeriod] = useState("weekly");
  // Helper: month names
  // monthNames already declared above, remove duplicate

  // Aggregate yearly data by month
  const yearlyData = React.useMemo(() => {
    if (assetLiabilityPeriod !== "yearly") return assetLiabilityBreakdown;
    // Group by month
    const monthMap = {};
    assetLiabilityBreakdown.forEach((item) => {
      const monthIdx = new Date(item.date).getMonth();
      if (!monthMap[monthIdx]) {
        monthMap[monthIdx] = {
          month: monthNames[monthIdx],
          assets: 0,
          liabilities: 0,
        };
      }
      monthMap[monthIdx].assets += Number(item.assets || 0);
      monthMap[monthIdx].liabilities += Number(
        item.liabilities || item.liability || 0,
      );
    });
    // Ensure all months present
    return monthNames.map(
      (name, idx) =>
        monthMap[idx] || { month: name, assets: 0, liabilities: 0 },
    );
  }, [assetLiabilityBreakdown, assetLiabilityPeriod]);

  // Monthly: aggregate by day (show all days with data)
  const monthlyData = React.useMemo(() => {
    if (assetLiabilityPeriod !== "monthly") return assetLiabilityBreakdown;
    const dayMap = {};
    assetLiabilityBreakdown.forEach((item) => {
      const day = item.day;
      if (!dayMap[day]) {
        dayMap[day] = { day: day, assets: 0, liabilities: 0 };
      }
      dayMap[day].assets += Number(item.assets || 0);
      dayMap[day].liabilities += Number(
        item.liabilities || item.liability || 0,
      );
    });
    // Sort by day and return as array
    return Object.values(dayMap).sort((a, b) => a.day - b.day);
  }, [assetLiabilityBreakdown, assetLiabilityPeriod]);

  // Weekly: show last 7 days data
  const weeklyData = React.useMemo(() => {
    if (assetLiabilityPeriod !== "weekly") return assetLiabilityBreakdown;
    // API returns data with label, date, assets, liabilities
    // Just return the raw data as is for weekly view (max 7 days)
    return assetLiabilityBreakdown.slice(0, 7).map((item) => ({
      day: item.label || item.day || "", // Use label from API (e.g., "Mon, Feb 24")
      assets: Number(item.assets || 0),
      liabilities: Number(item.liabilities || item.liability || 0),
    }));
  }, [assetLiabilityBreakdown, assetLiabilityPeriod]);
  // Fetch Asset vs Liability Breakdown on mount

  // Asset/Liability donut filter state
  const [assetDistEntityType, setAssetDistEntityType] = useState("all");

  // Helper to get donut data based on filter
  function getAssetLiabilityDonutData() {
    // If you have liabilityDistribution, use it here. For now, only assets are shown.
    if (assetDistEntityType === "all" || assetDistEntityType === "assets") {
      return assetDistributionChart;
    }
    // If you want to support liabilities, add logic here.
    return [];
  }

  React.useEffect(() => {
    setAssetLiabilityBreakdownLoading(true);
    setAssetLiabilityBreakdownError(null);
    api
      .get(
        `/dashboard/charts/asset-liability-breakdown?period=${assetLiabilityPeriod}`,
        {
          headers: getAuthHeaders(),
        },
      )
      .then((res) => {
        const data = res.data || res;
        const breakdownData = Array.isArray(data.data) ? data.data : [];

        // Debug: Log the raw data to check structure
        console.log(
          "Asset-Liability Breakdown API Response:",
          JSON.stringify(breakdownData).slice(0, 500),
        );

        // Fix: Filter to show only last 30 entries and normalize abnormal values
        // The backend has bugs causing abnormal spikes, so we need to handle this

        // First, find the median asset value to determine the scale
        const assetValues = breakdownData
          .map((item) => Number(item.assets || 0))
          .filter((v) => v > 0 && v < 1000000000);
        const medianAssets =
          assetValues.length > 0
            ? assetValues.sort((a, b) => a - b)[
                Math.floor(assetValues.length / 2)
              ]
            : 100000;

        // Calculate scaling factor based on median
        const SCALE_THRESHOLD = 1000000000; // 1 billion
        const scaleFactor =
          medianAssets > 0 && medianAssets < SCALE_THRESHOLD
            ? 1
            : medianAssets > 0
              ? SCALE_THRESHOLD / medianAssets / 10
              : 1;

        const recentData = breakdownData
          .slice(-30) // Only show last 30 entries
          .map((item, index) => {
            let assets = Number(item.assets || 0);
            let liabilities = Number(item.liabilities || item.liability || 0);

            // Apply scaling if values are abnormally high
            if (assets > SCALE_THRESHOLD || liabilities > SCALE_THRESHOLD) {
              assets = assets / scaleFactor;
              liabilities = liabilities / scaleFactor;
            }

            return {
              ...item,
              day: index + 1, // Reset day numbering
              assets: Math.round(assets),
              liabilities: Math.round(liabilities),
            };
          });

        // Debug: Log normalized data
        console.log("Normalized Data sample:", recentData.slice(0, 3));

        setAssetLiabilityBreakdown(recentData);
        setAssetLiabilityBreakdownLoading(false);
      })
      .catch((err) => {
        setAssetLiabilityBreakdownError(
          "Failed to fetch asset-liability breakdown",
        );
        setAssetLiabilityBreakdownLoading(false);
      });
  }, [assetLiabilityPeriod]);
  // Net Worth period state (move to top for correct initialization)
  const [netWorthPeriod, setNetWorthPeriod] = useState("weekly");
  // Under Performing Assets state
  const [underPerformingAssets, setUnderPerformingAssets] = useState([]);
  const [underPerformingAssetsLoading, setUnderPerformingAssetsLoading] =
    useState(false);
  const [underPerformingAssetsError, setUnderPerformingAssetsError] =
    useState(null);
  // Fetch Under Performing Assets on mount
  React.useEffect(() => {
    setUnderPerformingAssetsLoading(true);
    setUnderPerformingAssetsError(null);
    api
      .get("/assets/analytics/underperforming?limit=3", {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const data = res.data || res;
        setUnderPerformingAssets(
          Array.isArray(data) ? data : data.assets || [],
        );
        setUnderPerformingAssetsLoading(false);
      })
      .catch((err) => {
        setUnderPerformingAssetsError(
          "Failed to fetch under performing assets",
        );
        setUnderPerformingAssetsLoading(false);
      });
  }, []);
  // Top Performing Assets state
  const [topAssets, setTopAssets] = useState([]);
  const [topAssetsLoading, setTopAssetsLoading] = useState(false);
  const [topAssetsError, setTopAssetsError] = useState(null);

  // Fetch Top Performing Assets on mount
  React.useEffect(() => {
    setTopAssetsLoading(true);
    setTopAssetsError(null);
    api
      .get("/assets/analytics/top-performing?limit=3", {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const data = res.data || res;
        setTopAssets(Array.isArray(data) ? data : data.assets || []);
        setTopAssetsLoading(false);
      })

      .catch((err) => {
        setTopAssetsError("Failed to fetch top performing assets");
        setTopAssetsLoading(false);
      });
  }, []);
  // Diversification Score state
  const [diversificationScore, setDiversificationScore] = useState(null);
  const [diversificationScoreLoading, setDiversificationScoreLoading] =
    useState(false);
  const [diversificationScoreError, setDiversificationScoreError] =
    useState(null);

  // Fetch Diversification Score on mount
  React.useEffect(() => {
    setDiversificationScoreLoading(true);
    setDiversificationScoreError(null);
    api
      .get("/dashboard/assets/diversification", {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const data = res.data || res;
        setDiversificationScore(data);
        setDiversificationScoreLoading(false);
      })
      .catch((err) => {
        setDiversificationScoreError("Failed to fetch diversification score");
        setDiversificationScoreLoading(false);
      });
  }, []);
  // Asset Distribution chart loading/error state
  const [assetDistributionChartLoading, setAssetDistributionChartLoading] =
    useState(false);
  const [assetDistributionChartError, setAssetDistributionChartError] =
    useState(null);
  const [assetDistPeriod, setAssetDistPeriod] = useState("weekly");
  const [assetDistType, setAssetDistType] = useState("all");
  const [assetTimeframe, setAssetTimeframe] = useState("Weekly");
  const [assetType, setAssetType] = useState("All");
  const [timeframe, setTimeframe] = useState("Weekly");
  // Month filter setup
  const allAvailableMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const currentMonth = monthNames[new Date().getMonth()];
  // Find index of current month in allAvailableMonths
  const currentMonthIndex = allAvailableMonths.indexOf(currentMonth);
  // Set initial scroll so current month is visible
  const initialMonthScroll = Math.max(
    0,
    Math.min(currentMonthIndex - 1, allAvailableMonths.length - 4),
  );
  const [monthScroll, setMonthScroll] = useState(initialMonthScroll);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [liabilityTimeframe, setLiabilityTimeframe] = useState("monthly");

  const assetColors = {
    Property: "#ec4899",
    Gold: "#3b82f6",
    Cash: "#6366f1",
    Crypto: "#f59e0b",
    Stocks: "#10b981",
  };
  const allDonutData = {
    Weekly: {
      All: [
        { name: "Property", value: 20, type: "Physical" },
        { name: "Gold", value: 15, type: "Physical" },
        { name: "Cash", value: 10, type: "Physical" },
        { name: "Crypto", value: 25, type: "Digital" },
        { name: "Stocks", value: 30, type: "Digital" },
      ],
      Physical: [
        { name: "Property", value: 20, type: "Physical" },
        { name: "Gold", value: 15, type: "Physical" },
        { name: "Cash", value: 10, type: "Physical" },
      ],
      Digital: [
        { name: "Crypto", value: 25, type: "Digital" },
        { name: "Stocks", value: 30, type: "Digital" },
      ],
    },

    Monthly: {
      All: [
        { name: "Property", value: 30, type: "Physical" },
        { name: "Gold", value: 22, type: "Physical" },
        { name: "Cash", value: 8, type: "Physical" },
        { name: "Crypto", value: 18, type: "Digital" },
        { name: "Stocks", value: 22, type: "Digital" },
      ],
      Physical: [
        { name: "Property", value: 30, type: "Physical" },
        { name: "Gold", value: 22, type: "Physical" },
        { name: "Cash", value: 8, type: "Physical" },
      ],
      Digital: [
        { name: "Crypto", value: 18, type: "Digital" },
        { name: "Stocks", value: 22, type: "Digital" },
      ],
    },

    Yearly: {
      All: [
        { name: "Property", value: 25, type: "Physical" },
        { name: "Gold", value: 20, type: "Physical" },
        { name: "Cash", value: 7, type: "Physical" },
        { name: "Crypto", value: 11, type: "Digital" },
        { name: "Stocks", value: 7, type: "Digital" },
      ],
      Physical: [
        { name: "Property", value: 25, type: "Physical" },
        { name: "Gold", value: 20, type: "Physical" },
        { name: "Cash", value: 7, type: "Physical" },
      ],
      Digital: [
        { name: "Crypto", value: 11, type: "Digital" },
        { name: "Stocks", value: 7, type: "Digital" },
      ],
    },
  };
  const filteredDonutData = useMemo(() => {
    return allDonutData[assetTimeframe][assetType];
  }, [assetType, assetTimeframe]);

  const barChartData = {
    Weekly: [
      { day: "Sun", value: 18000 },
      { day: "Mon", value: 22000 },
      { day: "Tue", value: 28000 },
      { day: "Wed", value: 34000 },
      { day: "Thu", value: 32000 },
      { day: "Fri", value: 28000 },
      { day: "Sat", value: 25000 },
    ],
    Monthly: [
      { day: "Week 1", value: 100000 },
      { day: "Week 2", value: 140000 },
      { day: "Week 3", value: 120000 },
      { day: "Week 4", value: 180000 },
    ],
    Yearly: [
      { day: "Jan", value: 400000 },
      { day: "Feb", value: 380000 },
      { day: "Mar", value: 510000 },
      { day: "Apr", value: 460000 },
      { day: "May", value: 520000 },
      { day: "Jun", value: 580000 },
      { day: "Jul", value: 620000 },
    ],
  };
  const CustomBarTooltip = ({ active, payload }) => {
    if (!active || !Array.isArray(payload) || payload.length === 0) {
      return null;
    }

    const rawValue = payload[0]?.value ?? payload[0]?.payload?.value;
    const value = Number(rawValue);

    if (!Number.isFinite(value)) {
      return null;
    }

    const currency = dashboardSummary?.currency || "USD";
    return (
      <div className="custom-tooltip">
        {currency} {formatLargeNumber(value, currency)}
      </div>
    );
  };
  // Fetch Asset Distribution chart data when period or assetType changes
  React.useEffect(() => {
    setAssetDistributionChartLoading(true);
    setAssetDistributionChartError(null);
    api
      .get(
        `/dashboard/charts/asset-distribution?assetType=${assetDistType}&type=${assetDistEntityType}`,
        {
          headers: getAuthHeaders(),
        },
      )
      .then((res) => {
        const data = res.data || res;
        setAssetDistributionChart(Array.isArray(data.data) ? data.data : []);
        setAssetDistributionChartLoading(false);
      })
      .catch((err) => {
        setAssetDistributionChartError(
          "Failed to fetch asset distribution chart",
        );
        setAssetDistributionChartLoading(false);
      });
  }, [assetDistPeriod, assetDistType, assetDistEntityType]);
  // Net Worth chart state
  const [netWorthChart, setNetWorthChart] = useState([]);
  const [netWorthChartLoading, setNetWorthChartLoading] = useState(false);
  const [netWorthChartError, setNetWorthChartError] = useState(null);

  // Net Worth Data
  const netWorthData = {
    weekly: [
      { day: "Mon", value: 5000 },
      { day: "Tue", value: 3500 },
      { day: "Wed", value: 3200 },
      { day: "Thu", value: 5200 },
      { day: "Fri", value: 1500 },
      { day: "Sat", value: 4000 },
      { day: "Sun", value: 4200 },
    ],
    monthly: [
      { day: "Week 1", value: 4000 },
      { day: "Week 2", value: 4500 },
      { day: "Week 3", value: 3800 },
      { day: "Week 4", value: 5000 },
    ],
    yearly: [
      { day: "Jan", value: 3500 },
      { day: "Feb", value: 4000 },
      { day: "Mar", value: 3800 },
      { day: "Apr", value: 4500 },
      { day: "May", value: 4200 },
      { day: "Jun", value: 4800 },
      { day: "Jul", value: 4600 },
      { day: "Aug", value: 5000 },
      { day: "Sep", value: 4900 },
      { day: "Oct", value: 5200 },
      { day: "Nov", value: 5100 },
      { day: "Dec", value: 5500 },
    ],
  };
  const lineChartData = {
    Weekly: [
      { month: "Aug", assets: 3500, liabilities: 1400 },
      { month: "Sep", assets: 4200, liabilities: 1600 },
      { month: "Oct", assets: 3800, liabilities: 1500 },
      { month: "Nov", assets: 4500, liabilities: 1800 },
    ],
    Monthly: [
      { month: "Aug", assets: 35000, liabilities: 14000 },
      { month: "Sep", assets: 42000, liabilities: 16000 },
      { month: "Oct", assets: 38000, liabilities: 15000 },
      { month: "Nov", assets: 45000, liabilities: 18000 },
    ],
    Yearly: [
      { month: "Jan", assets: 300000, liabilities: 120000 },
      { month: "Feb", assets: 320000, liabilities: 128000 },
      { month: "Mar", assets: 350000, liabilities: 140000 },
      { month: "Apr", assets: 380000, liabilities: 152000 },
      { month: "May", assets: 400000, liabilities: 160000 },
    ],
  };

  // Monthly: always show Week 1-4
  const monthlyNetWorthData = React.useMemo(() => {
    if (netWorthPeriod !== "monthly" || !Array.isArray(netWorthChart))
      return netWorthData["monthly"];
    // Group by week number (assume API items have a 'date' field)
    const weekMap = { 1: 0, 2: 0, 3: 0, 4: 0 };
    netWorthChart.forEach((item) => {
      if (!item.date) return;
      const d = new Date(item.date);
      const weekNum = Math.ceil(d.getDate() / 7); // 1-7: Week 1, 8-14: Week 2, etc.
      if (weekNum >= 1 && weekNum <= 4) {
        weekMap[weekNum] += item.value || 0;
      }
    });
    return [
      { day: "Week 1", value: weekMap[1] },
      { day: "Week 2", value: weekMap[2] },
      { day: "Week 3", value: weekMap[3] },
      { day: "Week 4", value: weekMap[4] },
    ];
  }, [netWorthChart, netWorthPeriod]);

  const weeklyNetWorthData = React.useMemo(() => {
    if (netWorthPeriod !== "weekly" || !Array.isArray(netWorthChart))
      return netWorthData[netWorthPeriod];
    const dayMap = {};
    netWorthChart.forEach((item) => {
      const d = new Date(item.date);
      const dayIdx = d.getDay() === 0 ? 6 : d.getDay() - 1; // JS: 0=Sun, 1=Mon...
      dayMap[dayIdx] = { day: weekDays[dayIdx], value: item.value };
    });
    return weekDays.map((name, idx) => dayMap[idx] || { day: name, value: 0 });
  }, [netWorthChart, netWorthPeriod]);

  // Fetch Net Worth chart data when period changes
  React.useEffect(() => {
    setNetWorthChartLoading(true);
    setNetWorthChartError(null);
    // P-3: the Weekly view shows this week's days (Mon-Sun), so it needs one point per day.
    // The API's "weekly" period returns one point per week, which cannot be placed on a weekday.
    const apiPeriod = netWorthPeriod === "weekly" ? "daily" : netWorthPeriod;
    api
      .get(`/dashboard/charts/networth?period=${apiPeriod}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const data = res.data || res;

        setNetWorthChart(
          Array.isArray(data.data) ? data.data : data.chart || [],
        );
        setNetWorthChartLoading(false);
      })
      .catch((err) => {
        setNetWorthChartError("Failed to fetch net worth chart");
        setNetWorthChartLoading(false);
      });
  }, [netWorthPeriod]);
  // Helper to format last updated date
  function formatLastUpdated(dateString) {
    if (!dateString) return "";
    const updatedDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - updatedDate;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    // Get local date parts
    const updatedDay = updatedDate.getDate();
    const updatedMonth = updatedDate.getMonth();
    const updatedYear = updatedDate.getFullYear();
    const nowDay = now.getDate();
    const nowMonth = now.getMonth();
    const nowYear = now.getFullYear();
    let label = "";
    if (
      updatedYear === nowYear &&
      updatedMonth === nowMonth &&
      updatedDay === nowDay
    ) {
      label = "Today";
    } else if (
      updatedYear === nowYear &&
      updatedMonth === nowMonth &&
      updatedDay === nowDay - 1
    ) {
      label = "Yesterday";
    } else {
      label = `${diffDays} days ago`;
    }
    let timeAgo = "";
    if (diffMins < 60) {
      timeAgo = `${diffMins} min ago`;
    } else if (diffMins < 60 * 24) {
      timeAgo = `${Math.floor(diffMins / 60)} hr ago`;
    } else {
      timeAgo = "";
    }
    return (
      <>
        {label}
        {timeAgo && <span className="stat-updated"> {timeAgo}</span>}
      </>
    );
  }

  // compute chartData based on period (use single variable)

  // Dashboard summary state
  const [dashboardSummary, setDashboardSummary] = useState({
    netWorth: null,
    totalAssets: null,
    totalLiabilities: null,
    lastUpdated: null,
    currency: null,
    netWorthChangePercentage: null,
  });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Fetch dashboard summary on mount
  React.useEffect(() => {
    setSummaryLoading(true);
    setSummaryError(null);

    // Fetch dashboard summary and liability count in parallel
    Promise.all([
      api.get("/dashboard/summary", { headers: getAuthHeaders() }),
      api.get("/liabilities?limit=1", { headers: getAuthHeaders() }),
    ])
      .then(([summaryRes, liabilitiesRes]) => {
        const data = summaryRes.data || summaryRes;
        // Get liability count from the liabilities API
        const liabilityCount =
          liabilitiesRes.data?.totalResults ||
          liabilitiesRes.data?.results?.length ||
          0;

        setDashboardSummary({
          netWorth: data.netWorth,
          totalAssets: data.totalAssets,
          // Override totalLiabilities count with correct value from liabilities API
          totalLiabilities: {
            ...data.totalLiabilities,
            count: liabilityCount,
          },
          lastUpdated: data.lastUpdated,
          currency: data.currency,
          netWorthChangePercentage: data.netWorthChangePercentage,
        });
        setSummaryLoading(false);
      })
      .catch((err) => {
        setSummaryError("Failed to fetch dashboard summary");
        setSummaryLoading(false);
      });
  }, []);
  const { user, loading, error, getUser } = useUser();
  // Allowed currency options (keep in sync with EditProfile)
  const allowedCurrencies = ["NZD", "AUD", "USD", "INR", "EUR"];

  // Guard reference to avoid re-entrancy
  const autoCurrencyUpdateRef = useRef(false);
  // Redux update in-progress flag
  const updating = useSelector((state) => state.updateUser?.updating);
  const dispatch = useDispatch();

  const [leftTableStatus, setLeftTableStatus] = useState("status");
  const [rightTableStatus, setRightTableStatus] = useState("status");

  // Ensure we fetch current user info when dashboard loads. Guarded so it only runs once.
  const didFetchMeRef = React.useRef(false);
  React.useEffect(() => {
    if (didFetchMeRef.current) return;
    didFetchMeRef.current = true;

    const token = localStorage.getItem("access_token");
    if (!token) return;

    // If useUser exposes getUser, prefer to call it so app state is hydrated consistently.
    if (typeof getUser === "function") {
      try {
        // call getUser but don't retrigger this effect if getUser identity changes
        // getUser may not return a Promise; wrap it so .catch is always available (ML-08)
        Promise.resolve(getUser(token)).catch((e) =>
          console.debug("getUser failed on dashboard mount", e),
        );
      } catch (e) {
        console.debug("getUser threw on dashboard mount", e);
      }
      return;
    }

    // Fallback: call /me directly and ignore result if not needed
    api
      .get("/me", { headers: getAuthHeaders() })
      .then(() => {})
      .catch((err) => {
        console.warn("Dashboard /me fetch failed", err);
        if (err?.response?.status === 401) {
          localStorage.removeItem("access_token");
        }
      });
  }, []);

  // Auto-detect currency and persist only when server /me has no currency
  useEffect(() => {
    // Do nothing until user is loaded
    if (!user || !user.id) return;

    // If user already has a currency set, skip
    const existingCurrency = user.defaultCurrency || user.currency || "";
    if (existingCurrency && existingCurrency.trim() !== "") {
      return;
    }

    // Prevent duplicate runs in same session
    if (autoCurrencyUpdateRef.current) return;
    autoCurrencyUpdateRef.current = true;

    // If a Redux update is already in progress, bail out to avoid race
    if (updating) {
      autoCurrencyUpdateRef.current = false;
      return;
    }

    (async () => {
      try {
        // Call ipapi to detect currency by IP
        const resp = await fetch("https://ipapi.co/json/");
        const data = await resp.json();
        let detected = "";
        if (data && data.currency)
          detected = String(data.currency).toUpperCase();

        // If detected currency not in allowed list, default to NZD
        const toSet = allowedCurrencies.includes(detected) ? detected : "NZD";

        // Dispatch update only if we have a value to set
        if (toSet) {
          await dispatch(
            updateUser({
              id: user.id || user._id,
              data: { currency: toSet, region: toSet },
            }),
          );
        }
      } catch (err) {
        console.warn("Auto currency detection failed", err);
      } finally {
        // release lock after small delay
        setTimeout(() => {
          autoCurrencyUpdateRef.current = false;
        }, 500);
      }
    })();
  }, [user, dispatch, updating]);

  // Phone verification modal logic
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [hasPhoneLinkError, setHasPhoneLinkError] = useState(false);
  const [isPhoneUnlinked, setIsPhoneUnlinked] = useState(false);
  // UX-03: once a closable prompt is dismissed, keep it closed for this browser
  // session instead of reopening it on every Dashboard visit. Only honoured while
  // the modal is closable, so mandatory verification is unaffected.
  const phoneModalDismissKey = user?.id ? `phone_modal_dismissed_${user.id}` : null;
  const [phoneModalDismissed, setPhoneModalDismissed] = useState(false);
  useEffect(() => {
    if (!phoneModalDismissKey) return;
    try {
      setPhoneModalDismissed(sessionStorage.getItem(phoneModalDismissKey) === "true");
    } catch (e) {
      setPhoneModalDismissed(false);
    }
  }, [phoneModalDismissKey]);
  // How long a 409 "already linked" conflict is allowed to make the modal
  // closable. Without an expiry, a user who hits this once and never resolves
  // it would get a permanent bypass of the mandatory verification rule.
  const PHONE_LINK_ERROR_GRACE_MS = 15 * 60 * 1000; // 15 minutes

  // Currency flash banner
  const [showCurrencyFlash, setShowCurrencyFlash] = useState(false);
  const currencyFlashTimer = useRef(null);
  const triggerCurrencyFlash = () => {
    const currency =
      localStorage.getItem("detectedCurrency") ||
      localStorage.getItem("user_currency");
    if (
      !currency ||
      localStorage.getItem("hasSeenCurrencyNotification") === "true"
    )
      return;
    setShowCurrencyFlash(true);
    localStorage.setItem("hasSeenCurrencyNotification", "true");
    localStorage.removeItem("detectedCurrency");
    currencyFlashTimer.current = setTimeout(
      () => setShowCurrencyFlash(false),
      5000,
    );
  };

  // Check for phone link error on mount (409 conflict case)
  useEffect(() => {
    // Note: We don't clear phone_link_error here - let MobileVerificationModal handle it
    // to display the custom error message
    const phoneLinkError = localStorage.getItem("phone_link_error");
    if (phoneLinkError) {
      setHasPhoneLinkError(true);
    }

    // Also check persistent flag, but only within a short grace window after
    // the conflict - otherwise this becomes a permanent bypass of mandatory
    // phone verification for anyone who ever hit a 409 once.
    const had409Error = localStorage.getItem("phone_link_error_persistent");
    if (had409Error === "true") {
      const persistentTime = parseInt(
        localStorage.getItem("phone_link_error_persistent_time") || "0",
        10,
      );
      const isWithinGrace =
        persistentTime &&
        Date.now() - persistentTime < PHONE_LINK_ERROR_GRACE_MS;
      if (isWithinGrace) {
        setHasPhoneLinkError(true);
      } else {
        // Expired override - clean up so it doesn't linger as a stale bypass
        localStorage.removeItem("phone_link_error_persistent");
        localStorage.removeItem("phone_link_error_persistent_time");
      }
    }
  }, []);

  // Check if phone verification is required - always show until verified
  useEffect(() => {
    // Wait until user is loaded
    if (loading) {
      console.log("PhoneModal: User data is loading, waiting...");
      return;
    }

    if (!user || !user.id) {
      console.log("PhoneModal: User not loaded yet");
      return;
    }


    // Check if phone is already verified - check user.identities (same source as EditProfile.js)
    const identities = Array.isArray(user?.identities) ? user.identities : [];
    const smsIdentities = identities.filter((i) => i.provider === "sms");
    const hasPhone = smsIdentities.length > 0;
    console.log("PhoneModal: Has phone?", hasPhone);

    if (hasPhone) {
      console.log("PhoneModal: User already has phone in profile, skipping");
      setPhoneVerified(true);
      setShowPhoneModal(false);
      // Clear the session flag since user has phone
      localStorage.removeItem("phone_verified");
      return;
    }

    // User has no phone in profile - show modal even if session says verified
    // This handles the case where user closed modal without adding phone
    // Check if user previously had phone (was verified) but now doesn't - indicates unlinking
    // Check both phone_verified (for old accounts) and phone_was_linked (for new unlink detection)
    const previouslyVerified = localStorage.getItem("phone_verified");
    const wasPreviouslyVerified = previouslyVerified === "true";
    const phoneWasLinked = localStorage.getItem("phone_was_linked") === "true";

    // If user had phone before but now doesn't, it's an unlink case - should not be closable
    // Only set to true if user actually had phone before (not for new users)
    if ((wasPreviouslyVerified || phoneWasLinked) && !hasPhone) {
      console.log(
        "PhoneModal: Phone was unlinked - modal not closable, wasPreviouslyVerified=",
        wasPreviouslyVerified,
        "phoneWasLinked=",
        phoneWasLinked,
      );
      setIsPhoneUnlinked(true);
    } else {
      console.log(
        "PhoneModal: Phone not unlinked - modal can be closable, wasPreviouslyVerified=",
        wasPreviouslyVerified,
        "phoneWasLinked=",
        phoneWasLinked,
        "hasPhone=",
        hasPhone,
      );
      setIsPhoneUnlinked(false);
    }

    console.log("PhoneModal: No phone in profile - showing modal");

    // Store timestamp when modal is first shown (for grace period calculation)
    // Only store if not already stored
    const firstModalShownAt = localStorage.getItem("phone_modal_first_shown");

    if (!firstModalShownAt) {
      // First time modal is being shown - store the timestamp
      localStorage.setItem("phone_modal_first_shown", Date.now().toString());
      console.log("PhoneModal: Stored first shown timestamp");
    }

    // Note: phone_was_linked is set in handlePhoneVerified when phone is successfully verified
    // and in EmailPhoneLink.js when phone is unlinked.
    // Don't set it here for first-time modal show to avoid false unlink detection.

    setShowPhoneModal(true);
    setPhoneVerified(false);
  }, [user, loading]);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    localStorage.removeItem("user_info");
    localStorage.removeItem("id_token");
    localStorage.removeItem("hideProfileBanner");
    localStorage.removeItem("profileCompletionPercent");
    localStorage.removeItem("pending_verification_email");
    localStorage.removeItem("user_profile");
    localStorage.removeItem("update_phone");
    localStorage.removeItem("phone");
    localStorage.removeItem("smsIdentity");
    localStorage.removeItem("update_phone_userId");
    navigate("/");
  };

  // Handle successful phone verification
  const handlePhoneVerified = async (phoneNumber) => {
    try {
      // Note: The add-identity API is now called in MobileVerificationModal after OTP verification
      // So we just need to refresh user data and update state

      // Mark as verified in localStorage
      localStorage.setItem("phone_verified", "true");
      localStorage.setItem("phone_was_linked", "true");
      // Clear the 409 error flag on successful verification so the mandatory
      // lock (stopper cursor) can re-engage correctly for future sessions
      localStorage.removeItem("phone_link_error_persistent");

      setPhoneVerified(true);
      setShowPhoneModal(false);
      setIsPhoneUnlinked(false); // Reset unlinked flag on successful verification
      setHasPhoneLinkError(false); // Reset 409 flag on successful verification

      // Refresh user data to get updated identities
      const token = localStorage.getItem("access_token");
      if (getUser) {
        getUser(token);
      }
    } catch (error) {
      console.error("Failed to handle phone verification:", error);
    }
  };

  // Handle phone linking success or error from CallbackPage
  useEffect(() => {
    const checkPhoneVerificationResult = () => {
      // Check for success flag
      const successTime = localStorage.getItem("phone_verified_success_time");
      if (successTime) {
        const timeDiff = Date.now() - parseInt(successTime, 10);
        if (timeDiff < 5000) {
          // Phone verified successfully - close modal and mark as verified
          localStorage.setItem("phone_verified", "true");
          setPhoneVerified(true);
          setShowPhoneModal(false);
          setIsPhoneUnlinked(false); // Reset unlinked flag on successful verification
          // Clear the 409 error flag on successful verification
          localStorage.removeItem("phone_link_error_persistent");
          localStorage.removeItem("phone_link_error_persistent_time");
        }
        localStorage.removeItem("phone_verified_success");
        localStorage.removeItem("phone_verified_success_time");
      }

      // Check for error flag (identity already linked)
      const errorTime = localStorage.getItem("phone_link_error_time");
      const errorMessage = localStorage.getItem("phone_link_error");

      if (errorTime && errorMessage) {
        // Store persistent flag for 409 error so it survives refresh, plus the
        // original conflict time so the override can expire (see below)
        localStorage.setItem("phone_link_error_persistent", "true");
        localStorage.setItem("phone_link_error_persistent_time", errorTime);
        // Mark that there was an error so modal can be closed
        setHasPhoneLinkError(true);
        // Note: Don't remove phone_link_error here - let MobileVerificationModal handle it
        // to display the custom error message
        localStorage.removeItem("phone_link_error_time");
      }

      // Check if user had 409 error before (persistent flag), but only within
      // a short grace window - otherwise this becomes a permanent bypass of
      // mandatory phone verification for anyone who ever hit a 409 once.
      const had409Error = localStorage.getItem("phone_link_error_persistent");
      if (had409Error === "true") {
        const persistentTime = parseInt(
          localStorage.getItem("phone_link_error_persistent_time") || "0",
          10,
        );
        const isWithinGrace =
          persistentTime &&
          Date.now() - persistentTime < PHONE_LINK_ERROR_GRACE_MS;
        if (isWithinGrace) {
          setHasPhoneLinkError(true);
        } else {
          // Expired override - clean up so it doesn't linger as a stale bypass
          localStorage.removeItem("phone_link_error_persistent");
          localStorage.removeItem("phone_link_error_persistent_time");
        }
      }
    };

    checkPhoneVerificationResult();
  }, []);

  // Asset Distribution Data
  const assetDistributionData = [
    { name: "Property", value: 25, color: "#FF6B9D" },
    // { name: 'Gold', value: 20, color: '#4D9FEC' },
    { name: "Crypto", value: 11, color: "#FF9F43" },
    { name: "Stocks", value: 7, color: "#26C6DA" },
    { name: "Cash", value: 7, color: "#9B8CFF" },
    { name: "Others", value: 30, color: "#67748E" },
  ];

  // Asset vs Liability Data
  const assetLiabilityData = {
    monthly: Array.from({ length: 31 }, (_, i) => ({
      day: i + 1,
      assets: 100 + Math.sin(i / 5) * 150 + Math.random() * 50,
      liability: 250 + Math.cos(i / 4) * 100 + Math.random() * 50,
    })),
    weekly: Array.from({ length: 7 }, (_, i) => ({
      day: i + 1,
      assets: 150 + Math.sin(i) * 100,
      liability: 280 + Math.cos(i) * 80,
    })),
    yearly: Array.from({ length: 12 }, (_, i) => ({
      day: i + 1,
      assets: 120 + Math.sin(i / 2) * 130,
      liability: 260 + Math.cos(i / 2.5) * 90,
    })),
  };

  // Top Performing Assets Data
  const assetsData = [
    {
      id: 1,
      name: "Land in Pune",
      value: 120000,
      change: 12,
      status: "active",
      updated: "5 min ago",
    },
    {
      id: 2,
      name: "Land in Pune",
      value: 120000,
      change: 12,
      status: "active",
      updated: "5 min ago",
    },
    {
      id: 3,
      name: "Land in Pune",
      value: 120000,
      change: 12,
      status: "in-active",
      updated: "5 min ago",
    },
  ];

  const getFilteredAssets = (status) => {
    if (status === "status") return topAssets;
    return topAssets.filter((asset) => asset.status === status);
  };

  const getFilteredUnderperformingAssets = (status) => {
    if (status === "status") return underPerformingAssets;
    return underPerformingAssets.filter((asset) => asset.status === status);
  };

  const [activeIndex, setActiveIndex] = useState(null);

  const handleMouseEnter = (_, index) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };
  // Yearly: always show Jan-Dec

  const yearlyNetWorthData = React.useMemo(() => {
    if (netWorthPeriod !== "yearly" || !Array.isArray(netWorthChart))
      return netWorthData["yearly"];
    // Group by month
    const monthMap = {};
    netWorthChart.forEach((item) => {
      if (!item.date) return;
      const monthIdx = new Date(item.date).getMonth();
      if (!monthMap[monthIdx]) {
        monthMap[monthIdx] = { day: monthNames[monthIdx], value: 0 };
      }
      monthMap[monthIdx].value += item.value || 0;
    });
    // Ensure all months present
    return monthNames.map(
      (name, idx) => monthMap[idx] || { day: name, value: 0 },
    );
  }, [netWorthChart, netWorthPeriod]);

  const chartData = React.useMemo(() => {
    if (!Array.isArray(netWorthChart))
      return netWorthData[netWorthPeriod] || [];

    if (netWorthPeriod === "daily") {
      // Show last 30 days, format as "Oct 01"
      const dayMap = {};
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      netWorthChart.forEach((item) => {
        if (!item || !item.date) return;
        const d = new Date(item.date);
        if (isNaN(d.getTime())) return;
        const dayStr = `${monthNames[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`;
        if (!dayMap[dayStr]) dayMap[dayStr] = { day: dayStr, value: 0 };
        // P-3: net worth is a balance, so keep the latest point (API sorts oldest first), never add points up
        const val = Number(item.value || 0);
        dayMap[dayStr].value = Number.isFinite(val) ? val : 0;
      });
      // Return last 30 days
      const last30Days = [];
      const today = new Date();
      for (let i = 29; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const dayStr = `${monthNames[d.getMonth()]} ${String(d.getDate()).padStart(2, "0")}`;
        last30Days.push(dayMap[dayStr] || { day: dayStr, value: 0 });
      }
      return last30Days;
    }

    if (netWorthPeriod === "weekly") {
      // P-3: place points on the user's local weekday (getUTCDay put NZ Sunday mornings on Saturday),
      // only for the current Mon-Sun week, keeping the latest point of each day
      const now = new Date();
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      monday.setDate(monday.getDate() - (monday.getDay() === 0 ? 6 : monday.getDay() - 1));
      const nextMonday = new Date(monday);
      nextMonday.setDate(monday.getDate() + 7);
      const dayMap = {};
      netWorthChart.forEach((item) => {
        if (!item || !item.date) return;
        const d = new Date(item.date);
        if (isNaN(d.getTime()) || d < monday || d >= nextMonday) return;
        const jsDay = d.getDay(); // 0=Sun..6=Sat (local)
        const dayIdx = jsDay === 0 ? 6 : jsDay - 1;
        const val = Number(item.value || 0);
        dayMap[dayIdx] = { day: weekDays[dayIdx], value: Number.isFinite(val) ? val : 0 };
      });
      return weekDays.map(
        (name, idx) => dayMap[idx] || { day: name, value: 0 },
      );
    }

    if (netWorthPeriod === "monthly") {
      // Show all months, fill with zero if no data
      const monthMap = {};
      netWorthChart.forEach((item) => {
        if (!item || !item.date) return;
        const d = new Date(item.date);
        if (isNaN(d.getTime())) return;
        const monthIdx = d.getMonth();
        if (!monthMap[monthIdx])
          monthMap[monthIdx] = { day: monthNames[monthIdx], value: 0 };
        // P-3: latest point wins. The 12-month range includes this month last year too, which was added
        // to this month's value.
        const val = Number(item.value || 0);
        monthMap[monthIdx].value = Number.isFinite(val) ? val : 0;
      });
      return monthNames.map(
        (name, idx) => monthMap[idx] || { day: name, value: 0 },
      );
    }

    if (netWorthPeriod === "yearly") {
      // Show last 5 years up to current year, fill with zero if no data
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 6;
      const years = Array.from({ length: 8 }, (_, i) => startYear + i);
      const yearMap = {};
      netWorthChart.forEach((item) => {
        if (!item || !item.date) return;
        const d = new Date(item.date);
        if (isNaN(d.getTime())) return;
        const year = d.getFullYear();
        if (!yearMap[year]) yearMap[year] = { year: String(year), value: 0 };
        const val = Number(item.value || 0);
        yearMap[year].value = Number.isFinite(val) ? val : 0; // P-3: latest point wins
      });
      return years.map(
        (year) => yearMap[year] || { year: String(year), value: 0 },
      );
    }

    return netWorthData[netWorthPeriod] || [];
  }, [
    netWorthChart,
    netWorthPeriod,
    monthlyNetWorthData,
    yearlyNetWorthData,
    monthNames,
  ]);

  const handleMonthScroll = (direction) => {
    if (direction === "up" && monthScroll > 0) {
      setMonthScroll(monthScroll - 1);
    } else if (
      direction === "down" &&
      monthScroll < allAvailableMonths.length - 4
    ) {
      setMonthScroll(monthScroll + 1);
    }
  };
  // (moved above, see month filter setup)
  const visibleMonths = allAvailableMonths.slice(monthScroll, monthScroll + 4);

  // Put this in your component (before return)
  const lineChartsData = React.useMemo(() => {
    const raw = Array.isArray(assetLiabilityBreakdown)
      ? assetLiabilityBreakdown
      : [];

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    // Normalize data: ensure both 'liabilities' and 'liability' keys are handled
    const normalizedRaw = raw.map((item) => ({
      ...item,
      assets: Number(item.assets || 0),
      // Handle both 'liabilities' (plural) and 'liability' (singular) from API
      liabilities: Number(item.liabilities || item.liability || 0),
    }));

    // --------------------------
    // WEEKLY → Show current week (Mon-Sun)
    // --------------------------
    // Get current week days (Monday to Sunday)
    const getCurrentWeekDays = () => {
      const now = new Date();
      const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
      const monday = new Date(now);
      monday.setDate(now.getDate() + mondayOffset);

      const weekDaysData = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        weekDaysData.push({
          day: weekDays[i], // Mon, Tue, Wed, etc.
          date: day.toISOString().split("T")[0], // YYYY-MM-DD for matching
          assets: 0,
          liabilities: 0,
        });
      }
      return weekDaysData;
    };

    // Map API data to current week days
    const weeklyMap = {};
    normalizedRaw.slice(0, 7).forEach((item) => {
      // Try to match by label or date
      let dayKey = item.label || "";
      // Extract day name from label if available (e.g., "Mon, Feb 24" -> "Mon")
      const dayName = dayKey.split(",")[0]?.trim() || "";

      // Try to find matching day in current week
      const weekDaysData = getCurrentWeekDays();
      const matchingDay = weekDaysData.find(
        (wd) =>
          wd.day === dayName ||
          (item.date && wd.date === item.date.split("T")[0]),
      );

      if (matchingDay) {
        weeklyMap[matchingDay.day] = {
          day: matchingDay.day,
          assets: item.assets,
          liabilities: item.liabilities,
        };
      }
    });

    // Fill in missing days with 0 values
    const weekly = weekDays.map(
      (day) => weeklyMap[day] || { day, assets: 0, liabilities: 0 },
    );

    // --------------------------
    // MONTHLY → Show Jan-Dec (current year)
    // --------------------------
    const currentYear = new Date().getFullYear();
    const monthlyMap = {};

    normalizedRaw.forEach((item) => {
      if (!item.date) return;

      const d = new Date(item.date);
      // Only include data from current year
      if (d.getFullYear() !== currentYear) return;

      const monthIndex = d.getUTCMonth(); // 0..11

      if (!monthlyMap[monthIndex]) {
        monthlyMap[monthIndex] = {
          month: monthNames[monthIndex],
          assets: 0,
          liabilities: 0,
        };
      }

      monthlyMap[monthIndex].assets += item.assets;
      monthlyMap[monthIndex].liabilities += item.liabilities;
    });

    // Ensure all 12 months are present
    const monthly = monthNames.map(
      (name, index) =>
        monthlyMap[index] || { month: name, assets: 0, liabilities: 0 },
    );

    // --------------------------
    // YEARLY → Show past 4 years + current year (5 years)
    // --------------------------
    const yearlyMap = {};
    const years = [];
    for (let i = 4; i >= 0; i--) {
      years.push(currentYear - i);
    }

    // Initialize yearly map with all years
    years.forEach((year) => {
      yearlyMap[year] = {
        year: year.toString(),
        assets: 0,
        liabilities: 0,
      };
    });

    normalizedRaw.forEach((item) => {
      if (!item.date) return;

      const d = new Date(item.date);
      const year = d.getFullYear();

      // Only include data from the 5 year range
      if (year >= years[0] && year <= years[4]) {
        yearlyMap[year].assets += item.assets;
        yearlyMap[year].liabilities += item.liabilities;
      }
    });

    const yearly = years.map((year) => yearlyMap[year]);

    return { monthly, yearly, weekly };
  }, [assetLiabilityBreakdown]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const currency = dashboardSummary?.currency || "USD";

    return (
      <div className="custom-line-tooltip">
        <p className="label">{label}</p>
        {payload.map((entry) => (
          <div key={entry.dataKey} className="tooltip-series-item">
            <span className="tooltip-series-name">
              {entry.name || entry.dataKey}
            </span>{" "}
            <span className="value">
              {currency} {formatLargeNumber(entry.value, currency)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Calculate if modal is closable based on account creation date (createdAt from /me API)
  // - If account is less than 7 days old, modal is closable
  // - If account is 7+ days old, modal is NOT closable (mandatory verification)
  const isModalClosable = React.useMemo(() => {
    // If there was a phone link error (identity already linked - 409), allow closing
    if (hasPhoneLinkError) {
      console.log("isModalClosable: true - 409 error");
      return true;
    }

    const now = new Date();

    // Check account age based on createdAt from /me API
    const accountCreatedAt = user?.createdAt;

    // If no createdAt, allow closing by default
    if (!accountCreatedAt) {
      console.log("isModalClosable: true - no createdAt");
      return true;
    }

    const createdDate = new Date(accountCreatedAt);
    const accountAgeDiff = now - createdDate;
    const accountAgeDays = Math.ceil(accountAgeDiff / (1000 * 60 * 60 * 24));

    // Account less than 7 days old = closable (days 0-6)
    // Account 7+ days old = NOT closable (mandatory)
    const isWithinGracePeriod = accountAgeDays < 7;

    // If phone was unlinked AND account is 7+ days old, then NOT closable
    // If within grace period (7 days), allow closing regardless of unlink status
    if (isPhoneUnlinked && !isWithinGracePeriod) {
      console.log(
        "isModalClosable: false - phone unlinked and past grace period",
      );
      return false;
    }

    // Default: closable if within 7-day grace period
    const isClosable = isWithinGracePeriod;

    console.log("isModalClosable calculation:", {
      createdAt: accountCreatedAt,
      accountAgeDays,
      isClosable,
      isWithinGracePeriod,
      isPhoneUnlinked,
      hasPhoneLinkError,
    });

    return isClosable;
  }, [user, hasPhoneLinkError, isPhoneUnlinked]);

  return (
    <>
      {/* Phone Verification Modal - render before loader so it's visible */}
      <MobileVerificationModal
        open={showPhoneModal && !phoneVerified && !(phoneModalDismissed && isModalClosable)}
        onClose={() => {
          console.log(
            "Modal onClose called, isModalClosable=",
            isModalClosable,
          );
          if (isModalClosable) {
            setShowPhoneModal(false);
            setPhoneModalDismissed(true);
            try {
              if (phoneModalDismissKey) sessionStorage.setItem(phoneModalDismissKey, "true");
            } catch (e) {
              // storage unavailable: the prompt simply returns on the next visit
            }
            triggerCurrencyFlash();
          }
        }}
        onVerified={(result) => {
          handlePhoneVerified(result);
          triggerCurrencyFlash();
        }}
        onLogout={handleLogout}
        isClosable={isModalClosable}
      />

      {loading && <Loader />}
      <CurrencyNotification />
      {showCurrencyFlash &&
        (() => {
          const currency = localStorage.getItem("user_currency") || "INR";
          const {
            getCurrencyDisplayName,
          } = require("../utils/currencyDetection");
          const displayName = getCurrencyDisplayName(currency);
          return (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                background: "linear-gradient(90deg, #4f46e5, #7c3aed)",
                color: "white",
                textAlign: "center",
                padding: "14px 48px 14px 16px",
                zIndex: 999999,
                fontSize: 15,
                fontWeight: 500,
                boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                animation: "slideDown 0.3s ease-out",
              }}
            >
              We've set your currency to{" "}
              <strong>
                {currency} ({displayName})
              </strong>
              . You can update it anytime on your{" "}
              <span
                onClick={() => {
                  setShowCurrencyFlash(false);
                  navigate("/editprofile");
                }}
                style={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Edit Profile
              </span>{" "}
              page.
              <button
                onClick={() => setShowCurrencyFlash(false)}
                style={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: 18,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          );
        })()}
      <Layout>
        <div className="dashboard-container">
          {/* 
           & Net Worth Section - 2 Column Flex */}
          <div className="two-column-section">
            {/* Top KPI Cards - 4 Column Grid */}
            <div className="kpi-wrap">
              {/* Net Worth Card */}
              <div className="kpi-card">
                <div className="kpi-label">
                  <h6>
                    <span className="kpi-label-text">Net Worth</span>
                    <InfoTooltip content="Net Worth is calculated as Total Assets minus Total Liabilities. The gauge indicates percentage change compared to last month." />
                  </h6>
                </div>
                <div className="kpi-body">
                  <div className="kpi-content">
                    <div className="kpi-value">
                      <h4>
                        <span>
                          {dashboardSummary.currency}
                        </span>{" "}
                        {dashboardSummary.netWorth !== null
                          ? formatLargeNumber(
                              dashboardSummary.netWorth,
                              dashboardSummary.currency,
                            )
                          : summaryLoading
                            ? "..."
                            : summaryError
                              ? "Error"
                              : "0"}
                      </h4>
                    </div>
                    <div className="kpi-change">
                      <p className="subtitle2 kpi-change-spacer" aria-hidden="true">0</p>
                    </div>
                  </div>
                  <GaugeChart
                    percentage={dashboardSummary?.netWorthChangePercentage}
                    color="#8b5cf6"
                    currency={dashboardSummary?.currency}
                  />
                </div>
              </div>

              {/* Total Assets Card */}
              <div className="kpi-card">
                <div className="kpi-label">
                  <h6>
                    <span className="kpi-label-text">Total Assets</span>
                    <InfoTooltip content="Total Assets represents the total value of all your owned assets. The gauge indicates percentage change compared to last month." />
                  </h6>
                </div>
                <div className="kpi-body">
                  <div className="kpi-content">
                    <div className="kpi-value">
                      <h4>
                        <span>
                          {dashboardSummary.currency}
                        </span>{" "}
                        {dashboardSummary?.totalAssets?.value !== undefined &&
                        dashboardSummary?.totalAssets?.value !== null
                          ? formatLargeNumber(
                              dashboardSummary.totalAssets.value,
                              dashboardSummary.currency,
                            )
                          : "0"}
                      </h4>
                    </div>
                    <div className="kpi-change">
                      <p className="subtitle2">
                        {dashboardSummary?.totalAssets?.count
                          ? `${dashboardSummary?.totalAssets?.count}`
                          : summaryLoading
                            ? "..."
                            : summaryError
                              ? "Error"
                              : "0"}
                      </p>
                    </div>
                  </div>
                  <GaugeChart
                    percentage={dashboardSummary?.totalAssets?.changePercentage}
                    color="#3b82f6"
                    currency={dashboardSummary?.currency}
                  />
                </div>
              </div>

              {/* Total Liabilities Card */}
              <div className="kpi-card">
                <div className="kpi-label">
                  <h6>
                    <span className="kpi-label-text">Total Liabilities</span>
                    <InfoTooltip content="Total Liabilities represents the total outstanding value of all owed liabilities. The gauge indicates percentage change compared to last month." />
                  </h6>
                </div>
                <div className="kpi-body">
                  <div className="kpi-content">
                    <div className="kpi-value">
                      <h4>
                        <span>
                          {dashboardSummary.currency}
                        </span>{" "}
                        {dashboardSummary?.totalLiabilities?.value !==
                          undefined &&
                        dashboardSummary?.totalLiabilities?.value !== null
                          ? formatLargeNumber(
                              dashboardSummary.totalLiabilities.value,
                              dashboardSummary.currency,
                            )
                          : "0"}
                      </h4>
                    </div>
                    <div className="kpi-change">
                      <p className="subtitle2">
                        {dashboardSummary?.totalLiabilities?.count
                          ? `${dashboardSummary?.totalLiabilities?.count}`
                          : summaryLoading
                            ? "..."
                            : summaryError
                              ? "Error"
                              : "0"}
                      </p>
                    </div>
                  </div>
                  <GaugeChart
                    percentage={
                      dashboardSummary?.totalLiabilities?.changePercentage
                    }
                    color="#f97316"
                    currency={dashboardSummary?.currency}
                  />
                </div>
              </div>

              {/* Last Updated Card */}
              <div className="kpi-card">
                <div className="kpi-label">
                  <h6>
                    <span className="kpi-label-text">Last Updated</span>
                    <InfoTooltip content="Last Updated indicates the most recent time your dashboard data was refreshed." />
                  </h6>
                </div>
                <div className="kpi-body">
                  <div className="kpi-content">
                    <div className="kpi-value">
                      <h4 style={{ color: "#10b981" }}>
                        {dashboardSummary.lastUpdated
                          ? formatLastUpdated(dashboardSummary.lastUpdated)
                          : summaryLoading
                            ? "..."
                            : "Today"}
                      </h4>
                    </div>
                    <div className="kpi-change">
                      <span>
                        <Clock size={14} />
                      </span>
                    </div>
                  </div>
                  <Clock size={85} strokeWidth={1.5} className="refresh-icon" />
                </div>
              </div>
            </div>
            {/* Asset Distribution Section (API-powered, beautiful UI) */}
            <div className="dash-card asset-distribution-card">
              <AssetDistributionChart />
            </div>
          </div>
          <div className="two-column-section">
            {/* Net Worth Bar Chart */}
            <div className="dash-card net-worth-card">
              <div className="dash-card-header">
                <div className="dash-card-header-deatail">
                  <h6 className="">
                    Net Worth
                    <InfoTooltip content="Asset distribution shows how your assets are allocated across different categories." />
                  </h6>
                  {/* <div className="net-worth-stats">
                    <div className="net-worth-amount">$152,400</div>
                    <div className="net-worth-change">+12.5% <span>vs Last Month</span></div>
                  </div> */}
                </div>
                <div className="filter-group">
                  <Select
                    value={{
                      value: netWorthPeriod,
                      label:
                        netWorthPeriod.charAt(0).toUpperCase() +
                        netWorthPeriod.slice(1),
                    }}
                    onChange={(option) => setNetWorthPeriod(option.value)}
                    options={[
                      { value: "daily", label: "Daily" },
                      { value: "weekly", label: "Weekly" },
                      { value: "monthly", label: "Monthly" },
                      { value: "yearly", label: "Yearly" },
                    ]}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isSearchable={false}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "36px",
                        fontSize: "14px",
                        fontWeight: 600,
                        borderRadius: "8px",
                        border: "1px solid var(--border-color, #CBD5E1)",
                        backgroundColor: "var(--bg-surface, #FFFFFF)",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "var(--heading-color, #0F172A)",
                      }),
                      option: (base) => ({
                        ...base,
                        fontSize: "14px",
                        fontWeight: 500,
                      }),
                    }}
                  />
                </div>
              </div>

              <svg width="100%" height="40" className="background-dots">
                <defs>
                  <pattern
                    id="dots"
                    x="0"
                    y="0"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <circle cx="10" cy="10" r="1.5" fill="#94a3b8" />
                  </pattern>
                </defs>
                <rect width="100%" height="40" fill="url(#dots)" />
              </svg>

              <div
                className="dashboard-chart-area"
                style={{ height: DASHBOARD_COMPARISON_CHART_HEIGHT }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={DASHBOARD_COMPARISON_CHART_MARGIN}
                  >
                  <defs>
                    {/* Default Gradient */}
                    <linearGradient
                      id="barGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#B1ACE9" />
                      <stop offset="100%" stopColor="#B1ACE9" />
                    </linearGradient>

                    {/* Hover Gradient */}
                    <linearGradient
                      id="barGradientHover"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#584BCE" />
                      <stop offset="100%" stopColor="#B1ACE9" />
                    </linearGradient>
                  </defs>

                  <XAxis
                    dataKey={netWorthPeriod === "yearly" ? "year" : "day"}
                    axisLine={false}
                    tickLine={false}
                    stroke="#94a3b8"
                    height={30}
                    style={{ fontSize: 12, fontWeight: 500 }}
                    interval={netWorthPeriod === "monthly" ? 0 : undefined}
                  />

                  <Tooltip content={<CustomBarTooltip />} cursor={false} />

                  <Bar
                    dataKey="value"
                    radius={[12, 12, 12, 12]}
                    maxBarSize={46}
                    minPointSize={8}
                    onMouseEnter={(_, index) => setActiveHover(index)}
                    onMouseLeave={() => setActiveHover(null)}
                  >
                    {chartData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          index === activeHover
                            ? "url(#barGradientHover)"
                            : "url(#barGradient)"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </div>
            </div>
            <div className="dash-card asset-liability-card">
              <div className="dash-card-header">
                <div className="dash-card-header-deatail">
                  <h6 className="">
                    Asset vs Liability Breakdown
                    <InfoTooltip content="Asset distribution shows how your assets are allocated across different categories." />
                  </h6>
                  <div className="chart-legend">
                    <div className="legend-item">
                      <div
                        className="legend-dot"
                        style={{ background: "#f97316" }}
                      />
                      <span className="subtitle2">Assets</span>
                    </div>
                    <div className="legend-item">
                      <div
                        className="legend-dot"
                        style={{ background: "#8b5cf6" }}
                      />
                      <span className="subtitle2">Liabilities</span>
                    </div>
                  </div>
                </div>
                <div className="filter-group">
                  <Select
                    value={{
                      value: assetLiabilityPeriod,
                      label:
                        assetLiabilityPeriod.charAt(0).toUpperCase() +
                        assetLiabilityPeriod.slice(1),
                    }}
                    onChange={(option) => setAssetLiabilityPeriod(option.value)}
                    options={[
                      { value: "weekly", label: "Weekly" },
                      { value: "monthly", label: "Monthly" },
                      { value: "yearly", label: "Yearly" },
                    ]}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isSearchable={false}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "36px",
                        fontSize: "14px",
                        fontWeight: 600,
                        borderRadius: "8px",
                        border: "1px solid var(--border-color, #CBD5E1)",
                        backgroundColor: "var(--bg-surface, #FFFFFF)",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "var(--heading-color, #0F172A)",
                      }),
                      option: (base) => ({
                        ...base,
                        fontSize: "14px",
                        fontWeight: 500,
                      }),
                    }}
                  />
                </div>
              </div>
              <div className="line-chart-wrapper">
                {/* <div className="month-filter-container">
              <button
                onClick={() => handleMonthScroll("up")}
                className="scroll-button scroll-up"
                disabled={monthScroll === 0}
              >
                <ChevronsUp size={16} />
              </button>

              <div className="months-list">
                {visibleMonths.map((month) => (
                  <button
                    key={month}
                    onClick={() => setSelectedMonth(month)}
                    className={`month-button ${selectedMonth === month ? "active" : ""}`}
                  >
                    {month}
                  </button>
                ))}
              </div>
              <button
                onClick={() => handleMonthScroll("down")}
                className="scroll-button scroll-down"
                disabled={monthScroll === allAvailableMonths.length - 4}
              >
                <ChevronsDown size={16} />
              </button>
            </div> */}

                <div
                  className="dashboard-chart-area line-chart-container"
                  style={{ height: DASHBOARD_COMPARISON_CHART_HEIGHT }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={
                        assetLiabilityPeriod === "weekly"
                          ? lineChartsData.weekly
                          : assetLiabilityPeriod === "yearly"
                            ? lineChartsData.yearly
                            : lineChartsData.monthly
                      }
                      margin={DASHBOARD_COMPARISON_CHART_MARGIN}
                    >
                      <defs>
                        {/* Assets gradient */}
                        <linearGradient
                          id="assetsGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          {/* Left white edge (0–5%) */}
                          <stop
                            offset="0%"
                            stopColor="transparent"
                            stopOpacity="1"
                          />

                          {/* Transition to main color */}
                          <stop
                            offset="5%"
                            stopColor="#f97316"
                            stopOpacity="1"
                          />
                          <stop
                            offset="95%"
                            stopColor="#f97316"
                            stopOpacity="1"
                          />

                          {/* Right white edge (95–100%) */}
                          <stop
                            offset="100%"
                            stopColor="transparent"
                            stopOpacity="1"
                          />
                        </linearGradient>
                        {/* Liabilities gradient */}
                        <linearGradient
                          id="liabilitiesGradient"
                          x1="0"
                          y1="0"
                          x2="1"
                          y2="0"
                        >
                          {/* Transition to main color */}
                          <stop
                            offset="5%"
                            stopColor="#8b5cf6"
                            stopOpacity="1"
                          />
                          <stop
                            offset="95%"
                            stopColor="#8b5cf6"
                            stopOpacity="1"
                          />
                          {/* Right white edge (95–100%) */}
                          <stop
                            offset="100%"
                            stopColor="transparent"
                            stopOpacity="1"
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid
                        strokeDasharray="0"
                        stroke="transparent"
                        vertical={false}
                      />
                      <XAxis
                        dataKey={
                          assetLiabilityPeriod === "yearly"
                            ? "year"
                            : assetLiabilityPeriod === "monthly"
                              ? "month"
                              : "day"
                        }
                        tickLine={false}
                        axisLine={false}
                        height={30}
                        tick={{ fill: "#94a3b8", fontSize: 12, fontWeight: 500 }}
                        interval="preserveStartEnd"
                        tickFormatter={(value) => {
                          if (assetLiabilityPeriod === "yearly") {
                            return value; // 2022, 2023, 2024...
                          } else if (assetLiabilityPeriod === "monthly") {
                            return value; // Jan, Feb, Mar...
                          }
                          return value; // Mon, Tue, Wed...
                        }}
                      />
                      <YAxis hide={true} />

                      <Tooltip
                        cursor={false}
                        content={<CustomTooltip />}
                        formatter={(value) =>
                          typeof value === "number"
                            ? value.toLocaleString()
                            : value
                        }
                      />

                      {/* ASSETS LINE (Orange) */}
                      <Line
                        name="Asset"
                        type="monotone"
                        dataKey="assets"
                        stroke="url(#assetsGradient)"
                        strokeWidth={1}
                        dot={false}
                        activeDot={{ r: 4, fill: "#f97316", strokeWidth: 1 }}
                      />

                      {/* LIABILITIES LINE (Purple) */}
                      <Line
                        name="Liability"
                        type="monotone"
                        dataKey="liabilities"
                        stroke="url(#liabilitiesGradient)"
                        strokeWidth={1}
                        dot={false}
                        activeDot={{ r: 4, fill: "#8b5cf6", strokeWidth: 1 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
          <div className="bottom-grid">
            {/* Top Performing Assets */}
            <div className="dash-card">
              <div className="dash-card-header">
                <h6 className="">
                  Top Perf. Assets
                  <InfoTooltip content="Asset distribution shows how your assets are allocated across different categories." />
                </h6>
                {/* <select value={topPerfStatus} onChange={(e) => setTopPerfStatus(e.target.value)} className="drop-filter">
                            <option value="">Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select> */}
              </div>

              <div className="assets-list">
                {getFilteredAssets(leftTableStatus).length > 0 ? (
                  getFilteredAssets(leftTableStatus).map((asset) => (
                    <div key={asset.id} className="asset-item">
                      <div className="asset-info">
                        <div className="asset-icon trending-up">
                          <TrendingUp size={16} />
                          <div
                            className={`status-dot ${asset.status === "active" ? "active-dot" : "inactive-dot"}`}
                          />
                        </div>
                         <div>
                           <h6 className="asset-name" title={asset.name}>{asset.name}</h6>
                           <p className="subtitle2">Updated 5 min ago</p>
                         </div>
                      </div>
                      <div className="asset-value">
                        <div className="amount">
                          {asset.totalReturn ? asset.totalReturn : "0"}
                        </div>
                        <div className="change positive">
                          +
                          {asset.performanceMetrics?.totalReturnPercentage
                            ? Math.round(
                                asset.performanceMetrics.totalReturnPercentage,
                              )
                            : 0}
                          %
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="asset-item">
                    <div className="asset-info">
                      <div className="asset-icon trending-up">
                        <TrendingUp size={16} />
                      </div>
                      <div>
                        <h6 className="asset-name">--</h6>
                        <p className="subtitle2">No performance data yet</p>
                      </div>
                    </div>
                    <div className="asset-value">
                      <div className="amount">--</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Under Performing Assets */}
            <div className="dash-card">
              <div className="dash-card-header">
                <h6 className="">
                  Under Perf. Assets
                  <InfoTooltip content="Asset distribution shows how your assets are allocated across different categories." />
                </h6>
                {/* <select value={underPerfStatus} onChange={(e) => setUnderPerfStatus(e.target.value)} className="drop-filter">
                            <option value="">Status</option>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select> */}
              </div>

              <div className="assets-list">
                {getFilteredUnderperformingAssets(rightTableStatus).length >
                0 ? (
                  getFilteredUnderperformingAssets(rightTableStatus).map(
                    (asset) => (
                      <div key={asset.id} className="asset-item">
                        <div className="asset-info">
                          <div className="asset-icon trending-down">
                            <TrendingDown size={16} />
                            <div
                              className={`status-dot ${asset.status === "active" ? "active-dot" : "inactive-dot"}`}
                            />
                          </div>
                           <div>
                             <h6 className="asset-name" title={asset.name}>{asset.name}</h6>
                             <p className="subtitle2">Updated 5 min ago</p>
                           </div>
                        </div>
                        <div className="asset-value">
                          <div className="amount">
                            {asset.totalReturn ? asset.totalReturn : "0"}
                          </div>
                          <p className="change negative">
                            {asset.performanceMetrics?.totalReturnPercentage
                              ? Math.round(
                                  asset.performanceMetrics
                                    .totalReturnPercentage,
                                )
                              : 0}
                            %
                          </p>
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <div className="asset-item">
                    <div className="asset-info">
                      <div className="asset-icon trending-down">
                        <TrendingDown size={16} />
                      </div>
                      <div>
                        <h6 className="asset-name">--</h6>
                        <p className="subtitle2">No performance data yet</p>
                      </div>
                    </div>
                    <div className="asset-value">
                      <div className="amount">--</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Diversification Score */}
            <div className="dash-card">
              <div className="dash-card-header">
                <div className="dash-card-header-deatail">
                  <h6 className="">
                    Diversification Score
                    <InfoTooltip content="Asset distribution shows how your assets are allocated across different categories." />
                  </h6>
                  <div className="diversification-stats">
                    {/* <div className="stat-item-group">
                                <span className="stat-label">{diversificationScore?.totalAssets}</span>
                                <span className="stat-name">Assets</span>
                              </div>
                              <div className="stat-item-group">
                                <span className="stat-label">{diversificationScore?.totalLiabilities}</span>
                                <span className="stat-name">Liabilities</span>
                              </div> */}
                  </div>
                </div>
              </div>

              <div className="diversification-container">
                <HalfCircleGauge percentage={diversificationScore?.score} />
                {/* <div className="diversification-badge"> <ArrowUp size={13} strokeWidth={3} /> 25%</div> */}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default Dashboard;
